/* eslint-disable @typescript-eslint/no-explicit-any */
import { Autocomplete, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/UrlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";
import { PartnerModel, TechnicianModel } from "../../Utils/Models";
import { handleChangeAutocomplete } from "../../Utils/Utils";
import { CheckInOrderProps } from "./CheckInMainPage";

export type CheckInPagesProps = {
  checkInOrder: CheckInOrderProps;
  handleChangeCheckInOrder: (checkInOrder: CheckInOrderProps) => void;
};

export default function CheckInPartnerTechnician(props: CheckInPagesProps) {
  const { checkInOrder, handleChangeCheckInOrder } = props;
  const [partners, setPartners] = useState<PartnerModel[]>([]);
  const [technicians, setTechnicians] = useState<TechnicianModel[]>([]);
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);

  useEffect(() => {
    getPartners();
  }, []);

  useEffect(() => {
    if (checkInOrder.partner?._id) {
      getTechniciansForPartner(checkInOrder.partner._id);
    } else {
      setTechnicians([]);
      // Clear technician selection when partner changes
      if (checkInOrder.partnerTechnician) {
        handleChangeCheckInOrder({
          ...checkInOrder,
          partnerTechnician: null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkInOrder.partner]);

  function getPartners() {
    getData(`${urlEnum.customers}?isPartner=true`).then((res: any) => {
      if (res.data.status === 200) {
        setPartners(res.data.result);
      }
    });
  }

  function getTechniciansForPartner(partnerId: string) {
    setLoadingTechnicians(true);
    getData(`${urlEnum.technicians}/partner/${partnerId}`)
      .then((res: any) => {
        if (res.data.status === 200) {
          setTechnicians(res.data.result);
        }
      })
      .finally(() => {
        setLoadingTechnicians(false);
      });
  }

  return (
    <div style={{ margin: "10px 0px" }}>
      <Typography
        style={{
          fontSize: 20,
          fontWeight: 400,
          marginBottom: 15,
          textAlign: "center",
        }}
      >
        {Vocabulary.choosePartnerAndTechnician || "Choose Partner and Technician"}
      </Typography>

      {/* Partner Selection */}
      <div style={{ marginBottom: 20 }}>
        <Autocomplete
          size="small"
          id="partner"
          freeSolo={false}
          disablePortal
          getOptionLabel={(option: any) =>
            `${option.companyName || option.name}`
          }
          isOptionEqualToValue={(option: any, value: any) =>
            option._id === value._id
          }
          options={partners}
          value={checkInOrder.partner}
          onChange={(event: any, value: any) => {
            handleChangeCheckInOrder(
              handleChangeAutocomplete(event, value, checkInOrder, "partner")
            );
          }}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              name="partner"
              label={Vocabulary.partner || "Partner"}
              variant="outlined"
            />
          )}
        />
      </div>

      {/* Technician Selection - only shown when partner is selected */}
      {checkInOrder.partner && (
        <div>
          <Autocomplete
            size="small"
            id="partnerTechnician"
            freeSolo={false}
            disablePortal
            loading={loadingTechnicians}
            getOptionLabel={(option: any) =>
              `${option.name}${option.phone ? ` - ${option.phone}` : ""}`
            }
            isOptionEqualToValue={(option: any, value: any) =>
              option._id === value._id
            }
            options={technicians}
            value={checkInOrder.partnerTechnician}
            onChange={(event: any, value: any) => {
              handleChangeCheckInOrder(
                handleChangeAutocomplete(
                  event,
                  value,
                  checkInOrder,
                  "partnerTechnician"
                )
              );
            }}
            fullWidth
            noOptionsText={
              technicians.length === 0
                ? Vocabulary.noTechniciansForPartner ||
                  "No technicians available for this partner"
                : Vocabulary.noOptions
            }
            renderInput={(params) => (
              <TextField
                {...params}
                name="partnerTechnician"
                label={Vocabulary.technician}
                variant="outlined"
              />
            )}
          />
        </div>
      )}
    </div>
  );
}
